<template lang="pug">
  .file-selecter(:style="{ width: width, minHeight: height }" :class="layout")
    .drag-area(
      v-show="showDragAndDrop"
      @dragleave.prevent="onPrevent()"
      @dragover.prevent="onPrevent()"
      @drop.prevent="dropFile($event)"
      :style="{ width: _dragAreaWidth, minHeight: _dragAreaHeight }")
      .drag-message ドラッグ＆ドロップ

    .select-area(:style="{ width: _selectAreaWidth, minHeight: _selectAreaHeight }")
      .file-name(:style="{ width: _fileNameWidth }")
        span.file-selected(v-if="_fileName") {{ _fileName }}
        span(v-else) ファイルが選択されていません
      .file-select
        label.select-btn.btn ファイルを選択
          // @click="(e) => { e.target.value = '' }"は同一ファイルを読込んだ際にonChange()が発火しない問題の対応
          input(@change="selectFile($event)" type="file" :accept="mimetype" ref="file" @click="(e) => { e.target.value = '' }")
      .file-delete
        button.trash-btn.btn(:class="{ 'disable': _disableDeleteButton }" :disabled="_disableDeleteButton" @click="onDelete()")
          .trash-icon
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";

export const FILE_SELECTER_LAYOUT = ["vertical", "horizontal"];

/**
 * ファイル選択コンポーネント
 */
@Component
export default class FileSelecter extends Vue {
  /** 親コンポーネントから渡されるファイル */
  @Prop({ required: true })
  value!: File;

  /** ファイル名 */
  @Prop({ required: true })
  fileName!: string;

  @Prop({
    default: () => true
  })
  showDragAndDrop!: boolean;

  /** コンポーネント横幅 */
  @Prop({
    default: () => {
      return "600px";
    },
    type: String
  })
  width!: string;

  /** コンポーネント縦幅 */
  @Prop({
    default: () => {
      return "100px";
    },
    type: String
  })
  height!: string;

  /**
   * レイアウト
   * vertical or horizontal
   */
  @Prop({
    default: () => {
      return FILE_SELECTER_LAYOUT[0];
    },
    validator: (layout) => {
      return FILE_SELECTER_LAYOUT.indexOf(layout) !== -1;
    },
    type: String
  })
  layout!: string;

  /** ファイルのmimeType */
  @Prop({
    required: true,
    type: String
  })
  mimetype!: string;

  @Prop()
  /** 削除ボタンの非活性化可否 */
  disableDeleteButton!: boolean | null;

  /**
   * ファイルドロップイベント処理
   *
   * 選択されたファイルオブジェクトを親コンポーネントに渡す
   */
  public dropFile(event: DragEvent): void {
    if (!event.dataTransfer) {
      return;
    }
    const files: FileList = event.dataTransfer.files;
    this.propagationFile(files);

    return;
  }

  /**
   * ファイル選択イベント処理
   *
   * ファイル選択ダイアログがOKでもキャンセルでも呼び出される
   * 選択されたファイルオブジェクトを親コンポーネントに渡す
   */
  public selectFile(event: Event): void {
    if (!event.target) {
      return;
    }
    // targetの型定義にFileがいないので、anyにしてからFileを取り出す
    const target: any = event.target;
    const files: FileList = target.files;
    this.propagationFile(files);

    return;
  }

  /**
   * ファイルオブジェクトを親コンポーネントに渡す
   *
   * @param files 選択されたファイルリスト
   */
  public propagationFile(files: FileList): void {
    // ファイル選択ダイアログでOKが押下されたらファイルが存在し、キャンセルならundefinedになっている
    // 読込んだファイルが存在する場合のみ、親コンポーネントに通知する
    const selectedFile: File = files[0];

    if (selectedFile) {
      this.$emit("input", { file: selectedFile, fileName: selectedFile.name });
      // 基本は使用しないが、watch以外の方法でイベントを渡されたい場合のために、イベント伝搬
      // 例えばイベントの編集画面のように、S3の画像とローカルの画像を混合して使用する場合等は$emitしたい
      this.$emit("on-select", selectedFile, this.$el.id);
    }

    return;
  }

  /**
   * ### 削除ボタンの非活性化可否を返す
   *
   * - Propで渡されている場合はそれを優先し、無い場合はファイル名があるかで判断する
   */
  get _disableDeleteButton(): boolean {
    if (this.disableDeleteButton != null) {
      return this.disableDeleteButton;
    } else {
      return this._fileName ? false : true;
    }
  }

  /**
   * ファイル削除ボタンイベント処理
   *
   * ファイル名を初期化して、親コンポーネントに削除ボタン押下を通知する
   */
  public onDelete(event: any): void {
    // ブラウザの仕様により、同じファイルをファイル選択ダイアログで選択しても、ファイル選択イベントが発生しない
    // これを解決するため、file要素のvalue値を削除のタイミングで空にする
    const file: Vue | Element | Vue[] | Element[] = this.$refs.file;
    if (file instanceof Element) {
      let fileElement: any = file;
      fileElement.value = "";
    }

    this.$emit("input", { file: null, fileName: "" });
    // 基本は使用しないが、watch以外の方法でイベントを渡されたい場合のために、イベント伝搬
    // 例えばイベントの編集画面のように、S3の画像とローカルの画像を混合して使用する場合等は$emitしたい
    this.$emit("on-delete", null, this.$el.id);

    return;
  }

  /**
   * ファイル名を返す
   */
  get _fileName(): string {
    return this.fileName;
  }

  /**
   * ドラッグエリアの横幅を返す
   * - 縦レイアウトならコンポーネントの横幅
   * - 横レイアウトならコンポーネントの横幅の半分
   */
  get _dragAreaWidth(): string {
    if (this.layout == FILE_SELECTER_LAYOUT[0]) {
      return this.width;
    } else {
      return "50%";
    }
  }

  /**
   * ドラッグエリアの縦幅を返す
   * - 縦レイアウトならコンポーネントの縦幅－マージン－ファイル選択エリア縦幅
   * - 横レイアウトならコンポーネントの縦幅
   */
  get _dragAreaHeight(): string {
    if (this.layout == FILE_SELECTER_LAYOUT[0]) {
      return `calc(${this.height} - 50px)`;
    } else {
      return this.height;
    }
  }

  /**
   * ファイル選択エリアの横幅を返す
   * - 縦レイアウトならコンポーネントの横幅
   * - 横レイアウトならコンポーネントの横幅の半分
   */
  get _selectAreaWidth(): string {
    if (this.layout == FILE_SELECTER_LAYOUT[0]) {
      return this.width;
    } else {
      return "50%";
    }
  }

  /**
   * ファイル選択エリアの縦幅を返す
   * - 縦レイアウトならコンポーネントの縦幅－マージン－ファイル選択エリア縦幅
   * - 横レイアウトならコンポーネントの縦幅
   */
  get _selectAreaHeight(): string {
    if (this.layout == FILE_SELECTER_LAYOUT[0]) {
      return "";
    } else {
      return this.height;
    }
  }

  /**
   * ファイル名の横幅を返す
   * - 縦レイアウトならコンポーネントの横幅－各ボタンの横幅
   * - 横レイアウトならコンポーネントの横幅
   */
  get _fileNameWidth(): string {
    if (this.layout == FILE_SELECTER_LAYOUT[0]) {
      return `calc(${this.width} - 170px)`;
    } else {
      return "100%";
    }
  }

  /**
   * ブラウザのイベント処理回避用
   * 処理は無し
   */
  private onPrevent(): void {
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/pc/_colors.scss";
@import "../../styles/pc/_mixin.scss";

.file-selecter {
  .drag-area {
    border: 2px dashed $form-border-color;
    color: $file-dragarea-font;
    background-color: $file-dragarea;
    border-radius: 0.25rem;
    // ドラッグエリア内のメッセージを縦横中央に配置する
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .select-area {
    .file-name {
      color: $border-darkgray;
      span {
        font-size: 16px !important;
      }

      .file-selected {
        color: $selected-filename-color;
      }
    }

    .file-select {
      .select-btn {
        font-size: 14px !important;
        min-width: 124px;
        margin: 0;
        height: 32px !important;
        padding: 4px 4px !important;
        input {
          // ブラウザの標準fileinputを使うのではなく、自作したいので非表示にする
          display: none;
        }
        cursor: pointer;
      }
    }

    .file-delete {
      .trash-icon {
        @include svg-icon("../../assets/trash.svg", $font-base-color, 24px, 24px);
      }
      .trash-btn {
        font-size: 16px !important;
        height: 32px !important;
        padding: 2px 5px !important;
      }
    }
  }

  /* レイアウトが縦と横が存在するため、個別にスタイルを指定する */
  &.horizontal {
    display: flex;
    align-items: stretch;
    flex-direction: row;

    .drag-area {
      flex: 1;
      // marginは全体で使われている8pxに合わせる
      margin-right: 8px;
    }
    .select-area {
      flex: 1;
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;

      .file-name {
        display: flex;
        align-items: flex-start;
        flex-direction: row;
        justify-content: flex-start;
        span {
          font-size: 16px !important;
        }
        & > * {
          // 単語の折り返しは，contentsが親contentsからはみ出る場合にしか適用されない
          // Flexではcontentsの大きさに応じて大きさが変化するため、
          // デフォルトだと単語の折り返しよりも優先されてしまうため、maw-widthを指定することで回避する
          min-width: 0;
        }
      }
      .file-select {
        .btn {
          margin-right: 8px;
        }
      }
      .file-delete {
        height: 34px;
      }
    }
  }

  &.vertical {
    display: flex;
    align-items: stretch;
    flex-direction: column;

    // レイアウト崩れ対策として、横幅が一定以上小さい場合は強制的に横幅を変える
    $vertical-width-min: 400px;
    @media (max-width: 900px) {
      width: $vertical-width-min !important;
    }

    .drag-area {
      width: 100%;
      margin-top: 4px;
      margin-bottom: 14px;
    }
    .select-area {
      display: flex;
      align-items: center;

      // 横表示と縦表示で要素の表示位置が異なるので、orderで要素の位置を入れ替える
      .file-name {
        order: 2;
        margin-left: 8px;
        @media (max-width: 900px) {
          // ファイル選択ボタンと削除ボタン分の170px減算したものが横幅
          width: calc(#{$vertical-width-min} - 170px);
        }
      }
      .file-select {
        order: 1;
      }
      .file-delete {
        order: 3;
        // 縦配置の場合は削除ボタンを右端に寄せる
        margin-left: auto;
      }
    }
  }
}
</style>
